import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { useEffect, useState } from 'react';
import { useUserPaymentMethods } from '@/hooks/user/use-user-payment-methods';
import clsx from 'clsx';
import { ExclamationTriangleIcon, MinusCircleIcon, PlusCircleIcon } from '@heroicons/react/20/solid';
import { usePlanSubscribe } from '@/hooks/plans/use-plan-subscribe';
import { usePlanAddPaymentMethod } from "@/hooks/plans/use-plan-add-payment-method";
import { useKeycloak } from '@react-keycloak/web';
import { PaymentMethod } from '@/components/payment-method';


interface StripeCheckoutProps {
  plan: {
    id: string,
    amount: number,
    priceId: string,
    product: {
      description: string,
      name: String
    }
  };
}

export function StripeCheckout({ plan } : StripeCheckoutProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const { data: paymentMethods, isLoading: loadingPaymentMethods } = useUserPaymentMethods();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | null>(null);
  const [addNewPaymentMethod, setAddNewPaymentMethod] = useState(false);
  const planSubscribe = usePlanSubscribe();
  const [checkoutSuccess, setCheckoutSuccess] = useState(false);
  const stripe = useStripe();
  const elements = useElements();
  const addPaymentMethod = usePlanAddPaymentMethod();
  const { keycloak } = useKeycloak();
  const [seconds, setSeconds] = useState(10);

  useEffect(() => {
    if (!checkoutSuccess) return;

    const countdown = setInterval(() => {
      setSeconds((s) => s - 1);
    }, 1000);

    const timeout = setTimeout(() => {
      keycloak.login({
        prompt: 'none',
        redirectUri: window.location.origin + '/plans',
      });
    }, 10000);

    return () => {
      clearInterval(countdown);
      clearTimeout(timeout);
    };
  }, [checkoutSuccess]);

  useEffect(() => {
    if (paymentMethods && paymentMethods.length > 0) {
      paymentMethods.forEach((method) => {
        if (method.default) {
          setSelectedPaymentMethod(method.id);
        }
      });
    }

    if (paymentMethods && paymentMethods.length === 0) {
      setAddNewPaymentMethod(true);
    }
  }, [paymentMethods]);
      

  const handleSubmitSub = async (e: any) => {
    e.preventDefault();

    setIsProcessing(true);

    let paymentMethodId = selectedPaymentMethod;

    if (addNewPaymentMethod && stripe && elements) {
      // If adding a new payment method, we need to handle the payment method creation
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required'
      });


      if (error) {
        // Handle error
        console.error("Payment failed", error);
        setIsProcessing(false);
        return;
      }

      // Use the payment method created by Stripe
      setSelectedPaymentMethod(setupIntent.payment_method);
      paymentMethodId = setupIntent.payment_method;

      await addPaymentMethod.mutateAsync({
        paymentMethodId: setupIntent.payment_method,
      });

    }

    await planSubscribe.mutateAsync({
      paymentMethodId: paymentMethodId,
      priceId: plan.priceId
    }).then(() => {
      setCheckoutSuccess(true);
      // keycloak.login({ prompt: 'login' });
    }).catch((error) => {
      // Handle error
      console.error("Subscription failed", error);
    });

    setIsProcessing(false);
  }

  const handlePaymentMethodToggle = () => {
    const currentState = addNewPaymentMethod;
    setAddNewPaymentMethod(!addNewPaymentMethod);
    if (currentState) {
      // set back to default payment method if adding new one
      if (paymentMethods && paymentMethods.length > 0) {
        paymentMethods.forEach((method) => {
          if (method.default) {
            setSelectedPaymentMethod(method.id);
          }
        });
      }
    } else {
      setSelectedPaymentMethod(null);
    }
  }

  return (
    <div className="bg-white relative">
      <div className="absolute top-0 left-0 hidden h-full w-1/2 bg-white lg:block" aria-hidden="true" />
      <div className="absolute top-0 right-0 hidden h-full w-1/2 bg-indigo-900 lg:block" aria-hidden="true" />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-x-16 lg:grid-cols-2 lg:px-8 lg:pt-16">
        <h1 className="sr-only">Checkout</h1>

        <section
          aria-labelledby="summary-heading"
          className="bg-indigo-900 py-12 text-indigo-300 md:px-10 lg:col-start-2 lg:row-start-1 lg:mx-auto lg:w-full lg:max-w-lg lg:bg-transparent lg:px-0 lg:pt-0 lg:pb-24"
        >
          <div className="mx-auto max-w-2xl px-4 lg:max-w-none lg:px-0">
            <h2 id="summary-heading" className="sr-only">
              Order summary
            </h2>

            <dl>
              <dt className="text-sm font-medium">
                {checkoutSuccess ? "Amount paid" : "Amount due"}
              </dt>
              <dd className="mt-1 text-3xl font-bold tracking-tight text-white">€{plan.amount/100}</dd>
            </dl>

            <dl>
              <dt className="mt-6 text-sm font-medium">
                Recuring payment
              </dt>
              <dd className="mt-1 text-3xl font-bold tracking-tight text-white">monthly</dd>
            </dl>

            <ul role="list" className="divide-y divide-white divide-opacity-10 text-sm font-medium">
              <li key={plan.id} className="flex items-start space-x-4 py-6">
                  {/* <img
                    src={product.imageSrc}
                    alt={product.imageAlt}
                    className="h-20 w-20 flex-none rounded-md object-cover object-center"
                  /> */}
                  <div className="flex-auto space-y-1">
                    <h3 className="text-white">{plan.product.name}</h3>
                    <p>{plan.product.description}</p>
                  </div>
                </li>
            </ul>

            <dl className="space-y-6 border-t border-white border-opacity-10 pt-6 text-sm font-medium">
              <div className="flex items-center justify-between">
                <dt>Subtotal</dt>
                <dd>€{plan.amount/100}</dd>
              </div>

              <div className="flex items-center justify-between">
                <dt>Taxes</dt>
                <dd>€0.00</dd>
              </div>

              <div className="flex items-center justify-between border-t border-white border-opacity-10 pt-6 text-white">
                <dt className="text-base">Total</dt>
                <dd className="text-base">€{plan.amount/100}</dd>
              </div>
            </dl>
          </div>
        </section>
        {!checkoutSuccess && (
        <section
          aria-labelledby="payment-and-shipping-heading"
          className="py-16 lg:col-start-1 lg:row-start-1 lg:mx-auto lg:w-full lg:max-w-lg lg:pt-0 lg:pb-24"
        >
          <h2 id="payment-and-shipping-heading" className="sr-only">
            Payment and billing details
          </h2>

          <form>
            <div className="mx-auto max-w-2xl px-4 lg:max-w-none lg:px-0">

              <div className="">
                <h3 className="text-lg font-medium text-gray-900">Payment method</h3>

                <div className="mt-6 grid grid-cols-3 gap-y-6 gap-x-4 sm:grid-cols-4">
                  <div className="col-span-3 sm:col-span-4">
                      {/* <CardElement className={"block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-3"} /> */}
                      {/* <PaymentElement /> */}
                      {!addNewPaymentMethod && paymentMethods && paymentMethods?.map((method) => {
                        return (
                          <div className="mt-5 cursor-pointer" key={method.id} onClick={() => setSelectedPaymentMethod(method.id)}>
                            <PaymentMethod method={method} format="slim" selected={selectedPaymentMethod === method.id} selectable={!addNewPaymentMethod} />
                          </div>
                        );
                      })}
                      {paymentMethods && paymentMethods.length > 0 && (
                        <div onClick={handlePaymentMethodToggle} className="mt-6 text-sm text-indigo-500 flex gap-1 items-center justify-end cursor-pointer">
                          {!addNewPaymentMethod && (
                            <>
                              Add New Payment Method <PlusCircleIcon className="w-5 h-5" />
                            </>
                          )}
                          {addNewPaymentMethod && (
                            <>
                              Use Existing Payment Method <MinusCircleIcon className="w-5 h-5" />
                            </>
                          )}
                        </div>
                      )}
                      {addNewPaymentMethod && (
                        <div className="mt-6">
                          <PaymentElement />
                        </div>
                      )}
                  </div>
                </div>
              </div>

              <div className="mt-10 flex justify-end border-t border-gray-200 pt-6">
                <button
                  type="submit"
                  className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-50"
                  onClick={(e) => handleSubmitSub(e)}
                >
                  {isProcessing &&
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </>
                  }
                  {!isProcessing &&
                    <>
                      Subscribe
                    </>
                  }
                </button>
              </div>
            </div>
          </form>
        </section>
        )}
        {checkoutSuccess && (
        <section>
          <div className="rounded-md border-yellow-400 bg-yellow-50 p-4 mb-6">
            <div className="flex">
              <div className="shrink-0">
                <ExclamationTriangleIcon aria-hidden="true" className="w-5 h-5 text-yellow-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  You will be redirected in {seconds} seconds
                </p>
              </div>
            </div>
          </div>
          <h2 className="text-sm font-medium text-indigo-600">Payment successful</h2>
          <p className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">Thanks for subscribing</p>
          <p className="mt-2 text-base text-gray-500">
            Your plan is being processed as we speak. You will receive a confirmation notification shortly once everything is setup.
          </p>
          <h3 className="text-sm font-medium text-gray-900 mt-6">Payment information</h3>
          {selectedPaymentMethod && (() => {
            const method = paymentMethods?.find(m => m.id === selectedPaymentMethod);
            if (!method) return null;

            return (
              <div className="mt-5">
                <PaymentMethod method={method} format="compact" />
              </div>
            );
          })()}

        </section>
        )}
      </div>
    </div>
  )
}
